import { FaShareAlt } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import useCustomNavigate from "../../../../../hooks/useCustomNavigate";
import { useOutletContext, useParams, useLocation } from "react-router-dom";
import { useMemo, useState } from "react";
import SharePropertyModal from "../../../../../Components/SharePropertyModal/SharePropertyModal";
import { APP_BASE_URL } from "../../../../../Utils/constants";
import useAsyncAction from "../../../../../hooks/usePropertyAction";
import { addInterestedUser } from "../../../../../services/propertyService";
import { getUserData, withAuthCheck } from "../../../../../Utils/localStorageUtils";

const FlatListingHeader = () => {
  const { goTo } = useCustomNavigate();
  const { propertyId } = useParams();
  const { propertyData } = useOutletContext();
  const userData = getUserData();
  const location = useLocation();

  const { execute, loading, data } = useAsyncAction(addInterestedUser); // ✅ Use the reusable hook


  const handleInterestClick = async (e) => {
    e.preventDefault();
    await withAuthCheck(async (userData) => {
      const response = await execute(propertyId, userData._id);

      if (response.success) {
        console.log("User is interested!");
      }
    })(goTo); // ✅ Pass `navigate`
  };

  const [isShareModalOpen, setShareModalOpen] = useState(false); // ✅ Modal State

  const { basicDetails, locationDetails, aboutProperty } = propertyData;
  const propertyType = basicDetails?.additionalOption?.value || "Property";
  const city = locationDetails?.city || "";
  const locality = locationDetails?.locality || "";
  const bedrooms = aboutProperty?.roomDetails?.bedrooms || "-";


  // ✅ Parse query parameters manually using URLSearchParams
  const queryParams = new URLSearchParams(location.search);
  const selectedImageId = queryParams.get("imageId");

  // ✅ Extract unique categories and count images in each category
  const categories = useMemo(() => {
    const categoryMap = new Map();

    propertyData?.mediaFiles?.forEach((file) => {
      if (!categoryMap.has(file.category)) {
        categoryMap.set(file.category, 1);
      } else {
        categoryMap.set(file.category, categoryMap.get(file.category) + 1);
      }
    });

    return Array.from(categoryMap.entries()).map(([category, count]) => ({
      category,
      count,
    }));
  }, [propertyData]);

  // ✅ Determine the selected category based on imageId
  const selectedCategory = useMemo(() => {
    if (!selectedImageId && categories.length > 0) {
      return categories[0]?.category; // Default to first category if no match
    }

    const matchedFile = propertyData?.mediaFiles?.find(
      (file) => file._id === selectedImageId
    );

    return matchedFile ? matchedFile.category : categories[0]?.category; // Default if no match
  }, [selectedImageId, propertyData, categories]);

  return (
    <div className="px-4 bg-black text-white">
      <div className="flex items-center justify-between px-4 pb-3">
        <div className="flex items-center gap-2">
          <IoClose
            className="text-[24px] cursor-pointer"
            onClick={() => goTo(`/propertydetail/${propertyId}`)}
          />
          <h2 className="text-lg font-semibold">
            {bedrooms} BHK {propertyType} For {basicDetails?.lookingTo} in{" "}
            {locality ? `${locality}, ` : ""}
            {city ? `${city}` : ""}
          </h2>
        </div>
        <div className="flex items-center gap-4">
          <FaShareAlt
            className="text-xl cursor-pointer"
            onClick={() => setShareModalOpen(true)} // ✅ Open modal on click
          />
          <button
            onClick={handleInterestClick}
            className="bg-red-600 text-white px-4 py-2 rounded-full font-semibold"
            disabled={loading} // ✅ Disable while loading
          >
            {loading ? "Processing..." : data?.success ? "Already Interested" : "Contact Owner"}

          </button>
        </div>
      </div>

      {/* ✅ Dynamic Category Tabs */}
      <div className="flex gap-6 px-4 py-2 border-b border-gray-700 text-sm font-semibold">
        {categories.map(({ category, count }) => (
          <span
            key={category}
            className={`pb-1 cursor-pointer ${selectedCategory === category ? "border-b-2 border-red-600" : ""
              }`}
            onClick={() => {
              const firstImageId = propertyData.mediaFiles.find(f => f.category === category)?._id;
              goTo(`/propertydetail/${propertyId}/imageswithdetail?imageId=${firstImageId}`);
            }}
          >
            {category} ({count})
          </span>
        ))}
      </div>


      {/* ✅ Share Property Modal */}
      {isShareModalOpen && (
        <SharePropertyModal
          isOpen={isShareModalOpen}
          onClose={() => setShareModalOpen(false)}
          shareUrl={`${APP_BASE_URL}/property/${propertyId}`} // ✅ Dynamic property link
        />
      )}

    </div>
  );
};

export default FlatListingHeader;
